import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'motion/react';
import { useState } from 'react';
import type { MediaAsset } from '../types/content';
import { ResponsiveImage } from './ResponsiveImage';

// Adapted from the 21st.dev / Motion Primitives Image Comparison component (demo 1466).
// The native range overlay is retained to add keyboard semantics absent from the source component.

interface ImageComparisonProps {
  before: MediaAsset;
  after: MediaAsset;
  beforeLabel?: string;
  afterLabel?: string;
}

export function ImageComparison({ before, after, beforeLabel = 'Antes', afterLabel = 'Depois' }: ImageComparisonProps) {
  const reduced = useReducedMotion();
  const [value, setValue] = useState(50);
  const raw = useMotionValue(50);
  const position = useSpring(raw, { stiffness: reduced ? 1000 : 340, damping: reduced ? 100 : 34 });
  const clip = useTransform(position, (current) => `inset(0 0 0 ${current}%)`);
  const left = useTransform(position, (current) => `${current}%`);

  const update = (next: number) => {
    const clamped = Math.max(0, Math.min(100, next));
    setValue(clamped);
    raw.set(clamped);
  };

  return (
    <div className="comparison" style={{ touchAction: 'pan-y' }}>
      <ResponsiveImage media={before} className="comparison__image" sizes="(max-width: 800px) 100vw, 58vw" />
      <motion.div className="comparison__after" style={{ clipPath: clip }}>
        <ResponsiveImage media={after} className="comparison__image" sizes="(max-width: 800px) 100vw, 58vw" />
      </motion.div>
      <span className="comparison__label comparison__label--before">{beforeLabel}</span>
      <span className="comparison__label comparison__label--after">{afterLabel}</span>
      <motion.div className="comparison__handle" style={{ left }} aria-hidden="true"><span><ChevronLeft size={16} /><ChevronRight size={16} /></span></motion.div>
      <input
        type="range"
        min="0"
        max="100"
        value={value}
        onChange={(event) => update(Number(event.target.value))}
        aria-label={`Comparar imagem ${beforeLabel.toLowerCase()} e ${afterLabel.toLowerCase()}`}
        aria-valuetext={`${100 - value}% da imagem ${afterLabel.toLowerCase()} visível`}
      />
    </div>
  );
}
